// «Ajusta el crédito» de la Cotización: plazo en chips y dos tarjetas, Enganche y Pago semanal.
// Una va fija (candado, editable) y la otra se calcula (morado); el cálculo lo resuelve la pantalla.
import { useRef, useState, type ReactNode } from 'react';
import { Lock, LockOpen, Minus, Plus } from 'lucide-react';
import { Button } from '@/kit';
import { formatMXN } from '@/shared/lib/format/money';
import type { FixedField } from '../linkedQuoteTerms';
import { AmountInput } from './AmountInput';
import { ChoiceChip } from './ChoiceChip';

/** Paso de los botones − y + del enganche y del pago semanal, en pesos. */
const DOWN_STEP = 100;
const PAY_STEP = 10;

const VALUE_INPUT = 'h-11 w-full min-w-0 rounded-[10px] text-right text-[20px] font-semibold';

function FixedPill() {
    return (
        <span className="inline-flex h-7 items-center gap-1.5 rounded-full bg-promo px-3 text-[11.5px] font-semibold text-white">
            <Lock className="size-3" strokeWidth={2.5} aria-hidden />
            Fijo
        </span>
    );
}

/** Tarjeta fija: − campo +, slider y su mínimo y máximo. */
function FixedCard({
    title,
    testId,
    inputId,
    label,
    value,
    onValueChange,
    onCommit,
    onStep,
    slider,
    rangeLabel,
    busy,
}: {
    title: string;
    testId: string;
    inputId: string;
    label: string;
    value: number;
    onValueChange: (pesos: number) => void;
    onCommit: () => void;
    onStep: (direction: 1 | -1) => void;
    slider: {
        min: number;
        max: number;
        label: string;
        onChange: (pesos: number) => void;
        onRelease: () => void;
    };
    rangeLabel: ReactNode;
    busy: boolean;
}) {
    // Morada como la calculada: las dos se leen siempre como un par.
    return (
        <div
            data-testid={testId}
            data-calculated="false"
            className="flex min-w-0 flex-col gap-3 rounded-[14px] border-2 border-promo bg-promo-soft p-4"
        >
            <div className="flex items-center justify-between gap-2">
                <label htmlFor={inputId} className="text-[14px] font-semibold">
                    {title}
                </label>
                <FixedPill />
            </div>
            <div className="flex items-center gap-2">
                <Button
                    size="icon"
                    variant="outline"
                    className="size-11 flex-none"
                    aria-label={`Bajar ${label}`}
                    disabled={busy}
                    onClick={() => {
                        onStep(-1);
                    }}
                >
                    <Minus aria-hidden />
                </Button>
                <div className="min-w-0 flex-1">
                    <AmountInput
                        id={inputId}
                        value={value}
                        aria-label={title}
                        className={VALUE_INPUT}
                        onValueChange={onValueChange}
                        onCommit={onCommit}
                    />
                </div>
                <Button
                    size="icon"
                    variant="outline"
                    className="size-11 flex-none"
                    aria-label={`Subir ${label}`}
                    disabled={busy}
                    onClick={() => {
                        onStep(1);
                    }}
                >
                    <Plus aria-hidden />
                </Button>
            </div>
            <input
                type="range"
                min={slider.min}
                max={slider.max}
                step={1}
                value={Math.min(Math.max(value, slider.min), slider.max)}
                aria-label={slider.label}
                onChange={(e) => {
                    slider.onChange(Number(e.target.value));
                }}
                onPointerUp={slider.onRelease}
                onKeyUp={slider.onRelease}
                className="w-full accent-promo"
            />
            <div className="text-[11.5px] text-muted-foreground tabular-nums">{rangeLabel}</div>
        </div>
    );
}

/** Tarjeta calculada: monto grande de solo lectura y «Fijar este». */
function CalculatedCard({
    title,
    testId,
    amount,
    sub,
    onFix,
    fixLabel,
    busy,
}: {
    title: string;
    testId: string;
    amount: number | null;
    sub: string;
    onFix: () => void;
    fixLabel: string;
    busy: boolean;
}) {
    return (
        <div
            data-testid={testId}
            data-calculated="true"
            className="flex min-w-0 flex-col gap-2 rounded-[14px] border border-promo-soft-border bg-promo-soft p-4"
        >
            <div className="flex items-center justify-between gap-2">
                <span className="text-[14px] font-semibold">{title}</span>
                <button
                    type="button"
                    disabled={busy || amount === null}
                    onClick={onFix}
                    className="inline-flex h-7 items-center gap-1.5 rounded-full border border-promo/40 bg-card px-3 text-[11.5px] font-semibold text-promo transition-colors hover:border-promo disabled:opacity-50"
                >
                    <LockOpen className="size-3" strokeWidth={2.5} aria-hidden />
                    {fixLabel}
                </button>
            </div>
            <div
                data-testid={`${testId}-amount`}
                className="mt-1 truncate text-[30px] font-bold leading-tight text-promo tabular-nums"
            >
                {amount === null ? '—' : formatMXN(amount)}
            </div>
            <div className="text-[12px] text-promo">calculado · {sub}</div>
        </div>
    );
}

export function LinkedQuoteCards({
    fixed,
    onFix,
    down,
    minDown,
    maxDown,
    rangeLabel,
    downShare,
    onDownChange,
    onDownCommit,
    weeks,
    terms,
    onWeeksCommit,
    pay,
    payTarget,
    payRange,
    onPayCommit,
    payTerms,
    notice,
    busy = false,
    fixLabel = 'Fijar este',
}: {
    fixed: FixedField;
    /** «Fijar este»: cambia cuál de los dos queda fijo. */
    onFix: (field: FixedField) => void;
    down: number;
    minDown: number;
    maxDown: number;
    /** Mínimo y máximo del enganche, con el mínimo tachado si una promo lo bajó. */
    rangeLabel: ReactNode;
    /** «20 % del equipo» o equivalente. */
    downShare: string;
    /** Mientras se arrastra la barra; no re-cotiza. */
    onDownChange: (pesos: number) => void;
    /** Al salir del campo, con Enter, al soltar la barra o con − y +: aquí se recalcula. */
    onDownCommit: (pesos: number) => void;
    weeks: number;
    terms: readonly number[];
    onWeeksCommit: (weeks: number) => void;
    /** Pago semanal vigente; `null` mientras no hay cotización. */
    pay: number | null;
    /** Pago semanal que el vendedor fijó; `null` mientras el enganche es el fijo. */
    payTarget: number | null;
    /** Pago más bajo y más alto posibles a este plazo. */
    payRange: { min: number; max: number } | null;
    onPayCommit: (pesos: number) => void;
    /** «12 cuotas fijas · tasa anual 150 %». */
    payTerms: string;
    notice: string | null;
    busy?: boolean;
    /** Texto del botón de la tarjeta calculada. */
    fixLabel?: string;
}) {
    // Lo tecleado se aplica al salir del campo o con Enter; mientras, el calculado no se mueve.
    const [downDraft, setDownDraft] = useState<number | null>(null);
    const [payDraft, setPayDraft] = useState<number | null>(null);
    const commitDownDraft = (): void => {
        if (downDraft !== null) onDownCommit(downDraft);
        setDownDraft(null);
    };
    // Soltar la barra sin moverla no re-cotiza.
    const downSlid = useRef(false);
    const slideDown = (pesos: number): void => {
        downSlid.current = true;
        onDownChange(pesos);
    };
    const releaseDown = (): void => {
        if (!downSlid.current) return;
        downSlid.current = false;
        onDownCommit(down);
    };
    const payValue = payDraft ?? payTarget ?? Math.round((pay ?? 0) * 100) / 100;
    const commitPayDraft = (): void => {
        if (payDraft !== null && payDraft > 0) onPayCommit(payDraft);
        setPayDraft(null);
    };
    const sortedTerms = [...terms].sort((a, b) => a - b);

    const downCard =
        fixed === 'down' ? (
            <FixedCard
                title="Enganche"
                testId="quote-card-down"
                inputId="quote-down"
                label="enganche"
                value={downDraft ?? down}
                onValueChange={setDownDraft}
                onCommit={commitDownDraft}
                onStep={(d) => {
                    setDownDraft(null);
                    onDownCommit(down + d * DOWN_STEP);
                }}
                slider={{
                    min: minDown,
                    max: maxDown,
                    label: 'Ajustar enganche',
                    onChange: slideDown,
                    onRelease: releaseDown,
                }}
                rangeLabel={rangeLabel}
                busy={busy}
            />
        ) : (
            <CalculatedCard
                title="Enganche"
                testId="quote-card-down"
                amount={down}
                sub={downShare}
                onFix={() => {
                    onFix('down');
                }}
                fixLabel={fixLabel}
                busy={busy}
            />
        );

    const payCard =
        fixed === 'pay' && payRange !== null ? (
            <FixedCard
                title="Pago semanal"
                testId="quote-card-pay"
                inputId="quote-pay"
                label="pago semanal"
                value={payValue}
                onValueChange={setPayDraft}
                onCommit={commitPayDraft}
                onStep={(d) => {
                    setPayDraft(null);
                    onPayCommit(Math.max(PAY_STEP, payValue + d * PAY_STEP));
                }}
                slider={{
                    min: Math.ceil(payRange.min),
                    max: Math.floor(payRange.max),
                    label: 'Ajustar pago semanal',
                    onChange: (pesos) => {
                        setPayDraft(null);
                        onPayCommit(pesos);
                    },
                    onRelease: () => undefined,
                }}
                rangeLabel={
                    <span className="flex justify-between gap-2">
                        <span>Mín. {formatMXN(payRange.min)}</span>
                        <span>Máx. {formatMXN(payRange.max)}</span>
                    </span>
                }
                busy={busy}
            />
        ) : (
            <CalculatedCard
                title="Pago semanal"
                testId="quote-card-pay"
                amount={pay}
                sub={payTerms}
                onFix={() => {
                    onFix('pay');
                }}
                fixLabel={fixLabel}
                busy={busy || payRange === null}
            />
        );

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
                <span id="quote-term-label" className="text-[12.5px] font-semibold">
                    Plazo
                </span>
                <div
                    role="group"
                    aria-labelledby="quote-term-label"
                    className="flex flex-wrap gap-2"
                >
                    {sortedTerms.map((t) => (
                        <ChoiceChip
                            key={t}
                            selected={t === weeks}
                            disabled={busy}
                            onSelect={() => {
                                onWeeksCommit(t);
                            }}
                        >
                            {t} sem
                        </ChoiceChip>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2">
                {downCard}
                {payCard}
            </div>
            <p role="status" aria-live="polite" className="min-h-4 text-[11.5px] text-warning">
                {notice}
            </p>
        </div>
    );
}
